import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { useWhoami, whoamiQueryKey } from '../../api/whoami';
import type { MePreferencesRequest, MeResponse } from '../types';

/** Query-key registry for the self-service profile. */
export const meKeys = {
  /** Invalidation root — prefix-matches every effective-scoped `me` query. */
  all: ['me'] as const,
  /** Per-effective-user key so an impersonation change re-partitions the cache. */
  forUser: (userId: string | undefined) => ['me', userId] as const,
};

/**
 * `GET /api/v1/me` — the caller's **effective** profile (DDR-PLT-016). The query
 * is keyed by `whoami.effective.userId`, so when impersonation starts/ends (which
 * invalidates `['whoami']` → the effective id flips), this hook automatically
 * re-partitions to a fresh key and refetches — a mounted profile/team screen can
 * never keep showing the previous identity's data (no PII leak across the
 * impersonation boundary). Gated until the effective id is known.
 */
export function useMe(enabled = true) {
  const { data: whoami } = useWhoami();
  const userId = whoami?.effective.userId;
  return useQuery({
    queryKey: meKeys.forUser(userId),
    enabled: enabled && !!userId,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<MeResponse>('/me', { signal });
      return data;
    },
  });
}

/**
 * `PUT /api/v1/me/preferences` — partial-merge preferences update. The caller
 * builds a changed-fields-only body. On success both `['me']` (prefix-matching
 * the effective-scoped key) and `['whoami']` are invalidated so the profile pane
 * and the shell (which reads `whoami.effective.preferredLanguage`) re-resolve
 * from the server. A language change also flips the live i18next locale — that
 * is driven by the caller (it owns `useUserLanguage`), not this hook.
 */
export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MePreferencesRequest) => {
      const { data } = await apiClient.put<MeResponse>('/me/preferences', body);
      return data;
    },
    onSuccess: () => {
      // Invalidate rather than seed: the `['me']` key carries the effective user
      // id, which the mutation body doesn't, so a prefix invalidation is the
      // correct (and simplest) way to refresh the active profile query.
      void qc.invalidateQueries({ queryKey: meKeys.all });
      void qc.invalidateQueries({ queryKey: whoamiQueryKey });
    },
  });
}
